import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export interface AsyncMultiSelectOption {
  id: string;
  label: string;
  sublabel?: string | null;
  thumbnail?: string | null;
}

interface AsyncMultiSelectProps {
  value: AsyncMultiSelectOption[];
  onChange: (next: AsyncMultiSelectOption[]) => void;
  /**
   * Fetch options for a given search query. Should return a stable array
   * sorted in the order it wants displayed. Triggered on input change
   * (debounced by the component). Return an empty array on no match.
   */
  search: (query: string) => Promise<AsyncMultiSelectOption[]>;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  loadingText?: string;
  className?: string;
  debounceMs?: number;
}

export function AsyncMultiSelect({
  value,
  onChange,
  search,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyText = 'No results',
  loadingText = 'Loading…',
  className,
  debounceMs = 250,
}: AsyncMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<AsyncMultiSelectOption[]>([]);
  const [loading, setLoading] = useState(false);
  const searchRef = useRef(search);
  searchRef.current = search;

  const selectedIds = useMemo(() => new Set(value.map((v) => v.id)), [value]);

  // Debounced fetch whenever the popover is open and the query changes.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const results = await searchRef.current(query);
        if (!cancelled) setOptions(results);
      } catch {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, debounceMs);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, query, debounceMs]);

  const toggle = useCallback(
    (option: AsyncMultiSelectOption) => {
      if (selectedIds.has(option.id)) {
        onChange(value.filter((v) => v.id !== option.id));
      } else {
        onChange([...value, option]);
      }
    },
    [onChange, selectedIds, value],
  );

  const removeChip = useCallback(
    (id: string) => onChange(value.filter((v) => v.id !== id)),
    [onChange, value],
  );

  return (
    <div className={cn('space-y-2', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal"
          >
            <span className="text-muted-foreground">
              {value.length === 0
                ? placeholder
                : `${value.length} selected`}
            </span>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[min(32rem,calc(100vw-2rem))] p-0 z-[100]" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={searchPlaceholder}
              value={query}
              onValueChange={setQuery}
            />
            <CommandList>
              {loading && (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  {loadingText}
                </div>
              )}
              {!loading && options.length === 0 && (
                <CommandEmpty>{emptyText}</CommandEmpty>
              )}
              {!loading && options.length > 0 && (
                <CommandGroup>
                  {options.map((opt) => {
                    const selected = selectedIds.has(opt.id);
                    return (
                      <CommandItem
                        key={opt.id}
                        value={opt.id}
                        onSelect={() => toggle(opt)}
                        className="gap-2 cursor-pointer"
                      >
                        {opt.thumbnail ? (
                          <img
                            src={opt.thumbnail}
                            alt=""
                            className="w-6 h-6 rounded-full object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-muted flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm truncate">{opt.label}</div>
                          {opt.sublabel && (
                            <div className="text-xs text-muted-foreground truncate">
                              {opt.sublabel}
                            </div>
                          )}
                        </div>
                        <Check
                          className={cn(
                            'ml-auto h-4 w-4',
                            selected ? 'opacity-100' : 'opacity-0',
                          )}
                        />
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((v) => (
            <span
              key={v.id}
              className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs"
            >
              {v.thumbnail && (
                <img
                  src={v.thumbnail}
                  alt=""
                  className="w-4 h-4 rounded-full object-cover"
                />
              )}
              <span className="truncate max-w-[14rem]">{v.label}</span>
              <button
                type="button"
                onClick={() => removeChip(v.id)}
                className="hover:text-destructive"
                aria-label="Remove"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default AsyncMultiSelect;
